import { useParams } from "react-router";
import { connect, useDispatch } from "react-redux";
import classNames from "classnames";
import { State } from "redux/store";
import { useEffect, useRef, useState } from "react";
import {
  CLUSTER_DETAILS_PAGE,
  DATA_PAGE,
  DATASET_CREATION_PAGE,
  IMAGE_DETAILS_PAGE,
} from "helpers/constants/pages";
import {
  getDataset,
  getExportCount,
  getExportDataArray,
  getFilteredSimilarityClusters,
  getNavigationCluster,
  getShowCart,
  getShowExportModal,
} from "redux/SingleDataset/selectors";
import { getUserInfo } from "redux/User/selectors";
import { getImageDataLoader } from "redux/Modals/selectors";
import ProgressBarLoader from "views/uikit/ProgressBarLoader";
import {
  getCreationStatus,
  getIsLoading,
  getIsNewDatasetReady,
  getNewDataset,
} from "redux/CreateDataset/selectors";

import {
  setShowCart,
  setShowExportModal,
  setShowSharingMenu,
} from "redux/SingleDataset/actions";
import { copyToClipboard, isOnPremUser } from "helpers/utility/utilities";
import { setEnrichedDatasetId } from "redux/Enrichment/actions";
import { isAnonymousUser, userLogout } from "redux/authReducer";
import EditableDatasetTitle from "../EditableDatasetTitle";
import ExportModal from "views/modals/ExportModal";
import ActionOptions from "./ActionOptions";
import TabSwitch from "./TabSwitch";
import { DatasetStatus, ExportModalSource, TabTypes } from "types";
import KebabIcon from "assets/icons/KebabIcon";
import ShareIcon from "assets/icons/ShareIcon";
import styles from "./style.module.scss";
import { getVisibleSubtasks } from "views/pages/DatasetCreationPage/DatasetCreationWizard/Steps/CreateDataset";
import { DatasetInfoIcon } from "../../pages/DatasetInventory/DatasetInfoIcon/DatasetInfoIcon";
import { useDatasetLastUpdated } from "./useDatasetLastUpdated";
import { TrainModel } from "./TrainModel/TrainModel";
import ReloadIcon from "assets/icons/ReloadIcon";
import ReindexModal from "./ReindexModal";
import ContainerHeaderLayout from "../ContainerHeaderLayout/ContainerHeaderLayout";
import { ShoppingCartIcon } from "../../../assets/icons/ShoppingCartIcon";
import { useDataPageRightPanelContext } from "../../providers/DataPageRightPanelProvider";
import { VLChatIcon } from "../../../assets/icons/VLChatIcon";
import { useLabelPropagationExport } from "hooks/LabelPropagation/useLabelPropagationExport";
import { useEvaluateLabelPropagationModal } from "hooks/LabelPropagation/useEvaluateLabelPropagationModal";
import EvaluateLabelPropagationModal from "views/modals/EvaluateLabelPropagationModal";
import { useLocation } from "react-router-dom";
import { DatasetSnapshotsButton } from "./DatasetSnapshots/DatasetSnapshotsButton";
import { App, Tooltip } from "antd";
import { DatasetShareButton } from "./DatasetShareOption/DatasetShareButton";
import DatasetStatusBadge from "./DatasetStatus";
import {
  ADD_MEDIA_TAB,
  DATASET_CREATION_V2,
  DATASET_SHARE,
  DATASET_SNAPSHOTS,
  DELETE_DATASET,
  ENRICHMENT_ENRICH_DATASET,
  TRAIN_MODEL,
  TRAIN_WORTHY,
  USER_MANAGEMENT,
  USER_NOTIFICATIONS,
  VL_CHAT,
} from "../../../helpers/constants/filters";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import { UserNotifications } from "./UserNotifications/UserNotifications";
import VLTooltip from "views/uikit/VLTooltip";
import { useUserConfig } from "hooks/useUserConfig";
import { useUserPermission } from "../../../hooks/useUserPermission";
import HealthStatus from "../HealthStatus";
import TextWithTooltip from "../TextWithTooltip";
import {
  Mode,
  useDeleteDatasetContext,
} from "../../providers/DeleteDatasetProvider/DeleteDatasetProvider";

const ShowLoader = ({
  dynamicPage,
  isImageLoading,
  isCardListLoading,
}: any) => {
  const isImageDetailsPage = dynamicPage === IMAGE_DETAILS_PAGE;
  const isClusterOrDataPage =
    dynamicPage === CLUSTER_DETAILS_PAGE || dynamicPage === DATA_PAGE;
  const isDatasetCrationPage = dynamicPage === DATASET_CREATION_PAGE;

  if (isImageDetailsPage) {
    return <ProgressBarLoader loading={isImageLoading} />;
  }

  if (isClusterOrDataPage || isDatasetCrationPage) {
    return <ProgressBarLoader loading={isCardListLoading} />;
  }

  return null;
};

const ContainerHeader = ({
  page = DATA_PAGE,
  dynamicPage = DATA_PAGE,
  isContentLoading = false,
  isCardListLoading = false,
  dataset,
  exportCount,
  showCart,
  isImageLoading,
  isCreateDatasetLoading,
  isNewDatasetReady,
  newDataset,
  creationStatus,
  userId,
  navigationClusterLoader = false,
  showExportModal,
  title,
  className,
  showAbort = true,
}: any) => {
  const { message } = App.useApp();
  const trainModelBehavior = useFeatureFlagService(TRAIN_MODEL);
  const displayDataIngestionTab = useFeatureFlagService(ADD_MEDIA_TAB).show;
  const sharingEnabled = useFeatureFlagService(DATASET_SHARE).enabled;
  const isTrainWorthy = useFeatureFlagService(TRAIN_WORTHY).enabled;
  const isVLChatEnabled = useFeatureFlagService(VL_CHAT).enabled;
  const isUserManagementEnabled =
    useFeatureFlagService(USER_MANAGEMENT).enabled;
  const location = useLocation();
  const isDatasetSnapshotEnabled =
    useFeatureFlagService(DATASET_SNAPSHOTS).show;

  const {
    setMode: setRightPanelMode,
    mode,
    showPreviousMode,
  } = useDataPageRightPanelContext();
  const isUserNotificationsEnabled =
    useFeatureFlagService(USER_NOTIFICATIONS).show;
  const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);
  const isDatasetConfigLoaded = !useUserConfig().isLoading;
  const canDeleteDataset = useUserPermission("can_delete");
  const isDeleteDatasetEnabled =
    useFeatureFlagService(DELETE_DATASET).enabled && canDeleteDataset;
  const isNewCreationEnabled =
    useFeatureFlagService(DATASET_CREATION_V2).enabled;

  const exportButtonRef = useRef<HTMLDivElement>(null);
  const optionsBtnRef = useRef<HTMLDivElement>(null);

  const enrichmentTabBehavior = useFeatureFlagService(
    ENRICHMENT_ENRICH_DATASET,
  );

  const dispatch = useDispatch();
  const { newDatasetId, enrichDatasetId, datasetId, viewsDatasetId } =
    useParams();

  const currentId = (datasetId ||
    newDatasetId ||
    enrichDatasetId ||
    viewsDatasetId) as string;

  const { isExportLabelPropagation: isEvaluateLabelPropation } =
    useLabelPropagationExport(datasetId);

  const {
    isOpen: isOpenEvaluateLabelPropagationModal,
    closeModal: handleCloseModalEvaluateLabelPropagation,
    openModal: handleEvaluateLabelPropagationOpenModal,
    handleRunEvaluation,
    form,
  } = useEvaluateLabelPropagationModal(datasetId);

  const isDataCreationPage = dynamicPage === DATASET_CREATION_PAGE;
  const dynamicIsCardListLoading = isDataCreationPage
    ? isCreateDatasetLoading
    : dynamicPage === DATA_PAGE
      ? isCardListLoading
      : navigationClusterLoader;
  const dynamicDataset = newDataset || dataset;
  const datasetLastUpdated = useDatasetLastUpdated(currentId);
  const canReindex = useUserPermission("can_reindex");
  const displayReindex =
    dynamicDataset?.status === DatasetStatus.PARTIAL_INDEX &&
    canReindex &&
    (!isOnPremUser() || isNewCreationEnabled);
  const [isReindexModalOpen, setIsReindexModalOpen] = useState(displayReindex);
  const [datasetStatus, setDatasetStatus] = useState<DatasetStatus | null>(
    null,
  );

  useEffect(() => {
    if (enrichDatasetId) {
      dispatch(setEnrichedDatasetId(enrichDatasetId));
    }
  }, [enrichDatasetId, dispatch]);

  useEffect(() => {
    let status: DatasetStatus | null = null;

    if (datasetId || enrichDatasetId || viewsDatasetId) {
      status = dataset?.status || null;
    } else if (newDatasetId) {
      status = (newDataset || dataset)?.status || null;
    }
    if (creationStatus?.secondaryStatus?.length) {
      const visible = getVisibleSubtasks(creationStatus.secondaryStatus);
      const lastInProgressIndex = visible.findIndex((s) => s.progress < 100);
      if (lastInProgressIndex !== -1) {
        status = visible[lastInProgressIndex].status as DatasetStatus;
      } else {
        status = creationStatus.status;
      }
    }
    setDatasetStatus(status);
  }, [
    datasetId,
    enrichDatasetId,
    newDatasetId,
    viewsDatasetId,
    newDataset,
    dataset,
    creationStatus,
  ]);

  const isUpdateMode = location.pathname.includes("/update");
  const showDynamicTabs = (): TabTypes[] => {
    let result: TabTypes[] = [];
    if (!datasetStatus) return result;
    if (!newDataset?.creating) {
      result.push(TabTypes.SAVED_VIEWS);
      result.push(TabTypes.DATA_EXPLORATION);
      if (enrichmentTabBehavior.show) {
        result.push(TabTypes.DATA_ENRICHMENT);
      }
    }
    if (displayDataIngestionTab) {
      result.push(TabTypes.DATA_INGESTION);
    }
    return result;
  };
  const isPublicDataset = dynamicDataset?.sample === "DEFAULT_SAMPLE";

  const showPublicHeader = isPublicDataset && !isContentLoading;
  const showCartButton = !isDataCreationPage;
  const showDescription = !showPublicHeader && !!dynamicDataset;
  const isCreator = !newDataset?.created_by || newDataset.created_by === userId;
  const isDatasetProcessing =
    (creationStatus?.status ?? newDataset?.status) === DatasetStatus.INDEXING;
  const showAbortButton =
    showAbort &&
    isDataCreationPage &&
    !showPublicHeader &&
    !isNewDatasetReady &&
    !isUpdateMode &&
    !newDataset?.parent_dataset_id &&
    isCreator &&
    isDatasetProcessing;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- used in JSX below
  const showAskChat = isVLChatEnabled && !newDatasetId && datasetId;

  const { deleteDataset } = useDeleteDatasetContext();

  const canExport = useUserPermission("can_export");
  const canViewShareList = useUserPermission("can_view");

  const showExportButton = !newDatasetId && canExport;
  const handleExportClick = () => {
    dispatch(
      setShowExportModal(
        !showExportModal ? ExportModalSource.CONTAINER_HEADER : null,
      ),
    );
  };

  const containerHeaderDetailClassnames = classNames(
    styles.containerHeaderDetails,
    {
      [styles.justifySpaceBetween]: isPublicDataset,
    },
  );

  const optionsButtonClassnames = classNames(styles.optionsButton, {
    [styles.selectedOptionsButton]: showOptionsDropdown,
  });

  const toggleShoppingCart = () => {
    dispatch(setShowCart(!showCart));
  };

  const toggleOptionsDropdown = () => {
    setShowOptionsDropdown(!showOptionsDropdown);
  };

  const handleAbortClick = () => {
    deleteDataset(currentId, Mode.Abort);
  };

  const handleShareBtnClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isAnonymousUser()) {
      return;
    }
    event.stopPropagation();
    dispatch(setShowSharingMenu(true));
  };

  const handleSignInClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    userLogout();
  };

  const handleDeleteOptionClick = () => {
    deleteDataset(currentId);
  };

  const handleCopyURLClick = () => {
    let urlToCopy = new URL(window.location.href);
    copyToClipboard(urlToCopy.toString());

    message.success("Copied to clipboard!");
  };

  const renderEditableTitle = () => {
    const isCreator = dynamicDataset?.created_by === userId;
    const isEditable = isOnPremUser() || isCreator;

    return isEditable ? (
      <EditableDatasetTitle
        title={dynamicDataset?.display_name || ""}
        datasetID={dynamicDataset?.id || ""}
        isContentLoading={isContentLoading}
        fontStyle={{ width: "310px" }}
      />
    ) : (
      <TextWithTooltip
        text={dynamicDataset?.display_name}
        className={styles.containerHeaderTitleText}
        fontSize={20}
        strong={true}
      />
    );
  };

  const canSendToTraining = useUserPermission("can_send_to_training");

  const renderTrainModelBtn = () => {
    if (
      !canSendToTraining ||
      !dataset?.id ||
      !trainModelBehavior.show ||
      page === DATASET_CREATION_PAGE
    ) {
      return null;
    }
    return <TrainModel isTrainWorthy={isTrainWorthy} dataset={dataset} />;
  };

  const renderAskChatBtn = () => {
    const handleClick = () => {
      if (mode === "chat") {
        showPreviousMode();
      } else {
        setRightPanelMode("chat");
      }
    };
    return (
      <div
        onClick={handleClick}
        className={classNames(styles.askChat, {
          [styles.active]: mode === "chat",
        })}
      >
        <VLChatIcon />
        Ask VL
      </div>
    );
  };

  const renderSnapshotBtn = () => {
    if (!isDatasetSnapshotEnabled || page === DATASET_CREATION_PAGE) {
      return null;
    }
    return (
      <DatasetSnapshotsButton
        className={styles.actionButton}
        datasetId={currentId!}
      />
    );
  };

  const renderUserNotificationsButton = () => {
    if (!isUserNotificationsEnabled || isDataCreationPage || !dataset?.id) {
      return null;
    }
    return (
      <UserNotifications
        className={styles.actionButton}
        datasetId={dataset.id}
      />
    );
  };

  const renderShoppingCartBtn = () => {
    if (!showCartButton) {
      return null;
    }

    if (exportCount > 0) {
      return (
        <div
          onClick={toggleShoppingCart}
          className={styles.actionButton}
          data-tooltip-id="export-selection-count-box"
        >
          <ShoppingCartIcon />
          <div className={styles.cartCount}>{exportCount}</div>
        </div>
      );
    }
  };

  const renderShareBtn = () => {
    if (!sharingEnabled || newDatasetId || !canViewShareList) {
      return <noscript />;
    }
    const shareBtn = (
      <Tooltip title="Share">
        <div onClick={handleShareBtnClick} className={styles.actionButton}>
          <ShareIcon />
        </div>
      </Tooltip>
    );
    if (isOnPremUser() && isUserManagementEnabled) {
      return (
        <DatasetShareButton
          className={styles.actionButton}
          datasetId={datasetId!}
        />
      );
    } else if (!isAnonymousUser()) {
      return shareBtn;
    } else {
      return (
        <VLTooltip
          placement="bottom"
          title={
            <div className={styles.shareTooltip}>
              <div className={styles.tooltipText}>
                Only signed-in users can share public Datasets. Please sign-up
                or sign-in to proceed
              </div>
              <div className={styles.signInBtn} onClick={handleSignInClick}>
                Sign In/Sign Up
              </div>
            </div>
          }
        >
          <ShareIcon />
        </VLTooltip>
      );
    }
  };

  const renderOptionsBtn = () => {
    if (newDatasetId || !dynamicDataset) {
      return <noscript />;
    }

    return (
      <div
        ref={optionsBtnRef}
        onClick={toggleOptionsDropdown}
        className={optionsButtonClassnames}
      >
        <KebabIcon />

        {showOptionsDropdown && (
          <div className={styles.actionOptionsPositioner}>
            <ActionOptions
              callerRef={optionsBtnRef}
              show={showOptionsDropdown}
              onToggle={toggleOptionsDropdown}
              onCopyClick={handleCopyURLClick}
              showDeleteOption={isDeleteDatasetEnabled && !isAnonymousUser()}
              onDeleteClick={handleDeleteOptionClick}
              showExportOption={showExportButton}
              onExportClick={handleExportClick}
              showEvaluateLabelPropationOption={isEvaluateLabelPropation}
              onEvaluateLabelPropagationClick={
                handleEvaluateLabelPropagationOpenModal
              }
            />
          </div>
        )}

        <div className={styles.exportModalPositioner}>
          <ExportModal
            showModal={showExportModal === ExportModalSource.CONTAINER_HEADER}
            openedFrom={ExportModalSource.CONTAINER_HEADER}
            callerButtonRef={exportButtonRef}
          />
        </div>
      </div>
    );
  };

  const renderEvaluateLabelPropagation = () => {
    return (
      <EvaluateLabelPropagationModal
        isOpen={isOpenEvaluateLabelPropagationModal}
        onClose={handleCloseModalEvaluateLabelPropagation}
        onRunEvaluation={handleRunEvaluation}
        form={form}
      />
    );
  };

  return (
    <ContainerHeaderLayout
      className={className}
      upperRow={
        <>
          <div className={styles.containerLeft}>
            <div className={containerHeaderDetailClassnames}>
              {showDescription && (
                <DatasetInfoIcon
                  dataset={dynamicDataset}
                  showDatasetInfo
                  datasetLastUpdated={datasetLastUpdated}
                />
              )}
              <div className={styles.containerHeaderTitleContainer}>
                {title || renderEditableTitle()}
              </div>
            </div>
          </div>
          {isDatasetConfigLoaded ? (
            <>
              <TabSwitch
                showTabs={showDynamicTabs()}
                dataset={dynamicDataset}
                className={styles.tabs}
              />
              <div className={styles.filler} />
              {dynamicDataset?.status && !isDataCreationPage && (
                <DatasetStatusBadge
                  status={dynamicDataset.status}
                  datasetId={dynamicDataset.id}
                  icon={displayReindex ? <ReloadIcon /> : undefined}
                  onClick={
                    displayReindex
                      ? () => setIsReindexModalOpen(true)
                      : undefined
                  }
                />
              )}
              {showPublicHeader && (
                <VLTooltip title="This is public read-only sample dataset">
                  <div className={styles.publicLabel}>Public</div>
                </VLTooltip>
              )}
              {showAbort && <div className={styles.actionsDivider}></div>}
              <div className={styles.actionButtons}>
                {showAbortButton && (
                  <div
                    className={styles.abortButton}
                    onClick={handleAbortClick}
                  >
                    Abort
                  </div>
                )}
                {renderTrainModelBtn()}
                <div className={styles.buttonGroup}>
                  {showAskChat && renderAskChatBtn()}
                  {renderUserNotificationsButton()}
                  {renderShoppingCartBtn()}
                  {renderSnapshotBtn()}
                  {renderShareBtn()}
                  {renderOptionsBtn()}
                  {renderEvaluateLabelPropagation()}
                </div>
              </div>
              <ReindexModal
                open={isReindexModalOpen}
                onClose={() => setIsReindexModalOpen(false)}
              />
              <HealthStatus />
            </>
          ) : null}
        </>
      }
      lowerRow={
        <>
          <ShowLoader
            dynamicPage={dynamicPage}
            isImageLoading={isImageLoading}
            isCardListLoading={dynamicIsCardListLoading}
          />
        </>
      }
    />
  );
};

const mapStateToProps = (state: State) => {
  return {
    dataset: getDataset(state),
    exportCount: getExportCount(state),
    exportData: getExportDataArray(state),
    showCart: getShowCart(state),
    clusters: getFilteredSimilarityClusters(state),
    navigationCluster: getNavigationCluster(state),
    isImageLoading: getImageDataLoader(state),
    isCreateDatasetLoading: getIsLoading(state),
    creationStatus: getCreationStatus(state),
    isNewDatasetReady: getIsNewDatasetReady(state),
    newDataset: getNewDataset(state),
    userId: getUserInfo(state).userId,
    showExportModal: getShowExportModal(state),
  };
};

export default connect(mapStateToProps)(ContainerHeader);
